import { useEffect, useState } from 'react';
import { Button, Divider, Radio, Switch } from 'antd';
import styled from '@emotion/styled';

import { colorConstants } from 'config/colorConstants';

export type KpiSortDirection = 'asc' | 'desc';

export type KpiSortOption = {
    key: string;
    label: string;
};

export type KpiSortingValue = {
    direction: KpiSortDirection;
    optionKey: string;
};

export const DEFAULT_KPI_SORT_OPTIONS: KpiSortOption[] = [
    { key: 'percentageChange', label: 'Sort by percentage change' },
    { key: 'incrementalChange', label: 'Sort by incremental change' },
    { key: 'originalValue', label: 'Sort by original value' },
    { key: 'finalValue', label: 'Sort by final value' },
];

type KpiSortingPanelProps = {
    title?: string;
    options?: KpiSortOption[];
    value: KpiSortingValue;
    onCancel: () => void;
    onApply: (value: KpiSortingValue) => void;
};

const StyledPanel = styled.div`
    width: 320px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 12px;
    background: ${colorConstants.white};
    box-shadow: 0 3px 6px -4px ${colorConstants.blackTransparent12},
        0 9px 28px 8px ${colorConstants.blackTransparent05};
`;

const StyledHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const StyledTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    color: ${colorConstants.axisLabelGrey};
`;

const StyledDirectionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    font-size: 14px;
    line-height: 22px;
    color: ${colorConstants.blackTitle};
`;

const StyledOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    .ant-radio-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .ant-radio-wrapper {
        display: flex;
        align-items: center;
        margin: 0;
        padding: 0 4px;
        min-height: 24px;
        border-radius: 6px;
        font-size: 14px;
        line-height: 22px;
        color: ${colorConstants.blackTransparent88};
    }

    .ant-radio-wrapper-checked {
        background: ${colorConstants.axisSelectedBg};
    }
`;

const StyledFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .ant-btn {
        height: 32px;
        padding: 0 16px;
        border-radius: 6px;
        box-shadow: none;
    }

    .ant-btn:hover,
    .ant-btn:focus,
    .ant-btn:active {
        border-color: inherit !important;
    }

    .ant-btn-default,
    .ant-btn-default:hover,
    .ant-btn-default:focus,
    .ant-btn-default:active {
        border-color: ${colorConstants.toolbarBorderGrey} !important;
        color: ${colorConstants.blackTransparent88};
        background: ${colorConstants.white};
    }

    .ant-btn-primary,
    .ant-btn-primary:hover,
    .ant-btn-primary:focus,
    .ant-btn-primary:active {
        border-color: ${colorConstants.violet} !important;
        background: ${colorConstants.violet};
        color: ${colorConstants.white};
    }
`;

const KpiSortingPanel = ({
    title = 'Sorting options',
    options = DEFAULT_KPI_SORT_OPTIONS,
    value,
    onCancel,
    onApply,
}: KpiSortingPanelProps) => {
    const [draftValue, setDraftValue] = useState<KpiSortingValue>(value);

    useEffect(() => {
        setDraftValue(value);
    }, [value]);

    const handleDirectionChange = (checked: boolean) => {
        setDraftValue((previous) => ({
            ...previous,
            direction: checked ? 'desc' : 'asc',
        }));
    };

    const handleOptionChange = (optionKey: string) => {
        setDraftValue((previous) => ({
            ...previous,
            optionKey,
        }));
    };

    const handleApply = () => {
        onApply(draftValue);
    };

    return (
        <StyledPanel>
            <div className="flex w-full flex-col gap-3">
                <StyledHeader>
                    <StyledTitle>{title}</StyledTitle>
                    <StyledDirectionRow>
                        <span>Asc</span>
                        <Switch size="small" checked={draftValue.direction === 'desc'} onChange={handleDirectionChange} />
                        <span>Desc</span>
                    </StyledDirectionRow>
                </StyledHeader>
                <Divider className="!m-0" />
                <StyledOptions>
                    <Radio.Group
                        value={draftValue.optionKey}
                        onChange={(event) => handleOptionChange(event.target.value)}
                    >
                        {options.map((option) => (
                            <Radio key={option.key} value={option.key}>
                                {option.label}
                            </Radio>
                        ))}
                    </Radio.Group>
                </StyledOptions>
            </div>
            <Divider className="!m-0" />
            <StyledFooter>
                <Button onClick={onCancel}>Cancel</Button>
                <Button type="primary" onClick={handleApply}>
                    Apply
                </Button>
            </StyledFooter>
        </StyledPanel>
    );
};

export default KpiSortingPanel;
